/** Pure spatial audio helpers — distance gain + stereo pan from sim positions. */

export type SpatialListener = {
  x: number;
  y: number;
  facing: number;
};

export type AttenuationOpts = {
  /** Distance (m) at which gain is still 1. */
  fullRadius?: number;
  /** Distance (m) at which gain reaches 0. */
  silentRadius?: number;
};

export const DEFAULT_FULL_RADIUS_M = 10;
export const DEFAULT_SILENT_RADIUS_M = 110;

/**
 * Map planar distance to a 0–1 gain.
 * Near = full volume; beyond silentRadius = absent.
 * Mid-range uses a squared falloff so distant threats stay quiet.
 */
export function distanceAttenuation(distance: number, opts: AttenuationOpts = {}): number {
  const full = opts.fullRadius ?? DEFAULT_FULL_RADIUS_M;
  const silent = opts.silentRadius ?? DEFAULT_SILENT_RADIUS_M;
  if (!Number.isFinite(distance) || distance <= full) return 1;
  if (distance >= silent) return 0;
  const t = (distance - full) / (silent - full);
  const remain = 1 - t;
  return remain * remain;
}

/**
 * Stereo pan in −1..1 from source relative to listener facing (0 = +X forward).
 * Positive = right of facing in a Y-up plane.
 */
export function stereoPan(listener: SpatialListener, sourceX: number, sourceY: number): number {
  const dx = sourceX - listener.x;
  const dy = sourceY - listener.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-6) return 0;
  // Right vector for facing θ: (sin θ, −cos θ)
  const right =
    (dx * Math.sin(listener.facing) + dy * -Math.cos(listener.facing)) / dist;
  return Math.max(-1, Math.min(1, right * 0.9));
}

export type SpatialMix = { gain: number; pan: number };

/** Combined attenuation + pan for a world-space one-shot. */
export function spatialMix(
  listener: SpatialListener,
  sourceX: number,
  sourceY: number,
  opts: AttenuationOpts = {},
): SpatialMix {
  const distance = Math.hypot(sourceX - listener.x, sourceY - listener.y);
  return {
    gain: distanceAttenuation(distance, opts),
    pan: stereoPan(listener, sourceX, sourceY),
  };
}

/**
 * Aggregate proximity presence from nearest threats (0–1).
 * Squared distance weights keep far contacts from flooding the bed.
 */
export function threatApproachIntensity(
  distances: readonly number[],
  opts: { hearRadius?: number; weights?: readonly number[] } = {},
): number {
  const hear = opts.hearRadius ?? 95;
  if (distances.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < distances.length; i += 1) {
    const d = distances[i]!;
    if (d >= hear) continue;
    const t = 1 - d / hear;
    const weight = opts.weights?.[i] ?? 1;
    sum += t * t * weight;
  }
  return Math.max(0, Math.min(1, sum * 0.55));
}
